import {messages} from '@smartmixin/i18n';
import {MessageDescriptor} from 'react-intl';
import {CalendarAggregation} from './types';

export const metricToMessage: Record<string, MessageDescriptor> = {
  T: messages.units.temperature,
  U: messages.units.humidity,
  FF: messages.units.wind,
  SLP: messages.units.pressure,
  RR_1H: messages.units.precipitation,
  RR_RATE: messages.units.rainRate,
  RR_TODAY: messages.units.dailyPrecipitation,
  RR_3H: messages.units.precipitation,
  RR_6H: messages.units.precipitation,
  RR_12H: messages.units.precipitation,
  RR_24H: messages.units.precipitation,
  SOLAR_RADIATION: messages.units.solarRadiation,
  UV: messages.units.uv,
  VIS: messages.units.visibility,
  AQI: messages.units.AQI,
  PM_1: messages.units.PM_1,
  PM_10: messages.units.PM_10,
  PM_2P5: messages.units.PM_2P5,
  LIGHTNING_STRIKE_COUNT: messages.units.lightningStrikeCount,
};

/**
 * Metric-specific aggregated titles ("Maximum temperature", …) reusing the
 * long-standing report strings; other aggregatable metrics fall back to
 * genericAggregationMessages with the metric name as a placeholder.
 */
export const metricAggregationMessages: Record<
  string,
  Partial<Record<CalendarAggregation, MessageDescriptor>>
> = {
  // Wind is the one metric whose calendar options are two measured columns
  // rather than three aggregations of one, so the title names the metric the
  // cells actually hold. 'min' is deliberately absent: index 1 of the wind
  // payload is the direction, and getCalendarValueChoices never offers it.
  FF: {
    avg: messages.units.avg_wind,
    max: messages.units.max_gust,
  },
  T: {
    avg: messages.reports.avgTemperature,
    min: messages.reports.minTemperature,
    max: messages.reports.maxTemperature,
  },
  U: {
    avg: messages.reports.avgHumidity,
    min: messages.reports.minHumidity,
    max: messages.reports.maxHumidity,
  },
  SLP: {
    avg: messages.reports.avgPressure,
    min: messages.reports.minPressure,
    max: messages.reports.maxPressure,
  },
};

export const genericAggregationMessages: Record<
  CalendarAggregation,
  MessageDescriptor
> = {
  avg: messages.reports.avgMetric,
  min: messages.reports.minMetric,
  max: messages.reports.maxMetric,
};

/**
 * Labels for the calendar's "displayed value" chip and its dialog rows.
 *
 * These are shorter than the screen titles above: the chip sits next to the
 * metric selector, so "Maximum" is enough for temperature. Wind is the
 * exception — its two options are two different measured metrics, so naming
 * only the aggregation would leave "Maximum" meaning gust on one screen and
 * peak temperature on another.
 */
const CALENDAR_CHOICE_LABELS: Record<
  string,
  Partial<Record<CalendarAggregation, MessageDescriptor>>
> = {
  FF: {avg: messages.units.avg_wind, max: messages.units.max_gust},
  // Netatmo's wind module, same two values under its own metric name.
  Wind: {avg: messages.units.avg_wind, max: messages.units.max_gust},
};

const PLAIN_CHOICE_LABELS: Record<CalendarAggregation, MessageDescriptor> = {
  avg: messages.common.average,
  min: messages.common.minimum,
  max: messages.common.maximum,
};

export const getCalendarChoiceLabel = (
  metric: string | undefined,
  aggregation: CalendarAggregation,
): MessageDescriptor =>
  (metric && CALENDAR_CHOICE_LABELS[metric]?.[aggregation]) ||
  PLAIN_CHOICE_LABELS[aggregation];

/**
 * Optional second line under a calendar choice, answering "of what, over what?".
 *
 * Only wind gets one. "Minimum" under a temperature heading is not ambiguous;
 * "Rafale max." is, because the reader cannot tell whether the number is the
 * peak of the hour or of the week. The copy reuses the phrasing chart view
 * already prints on this same screen (ChartContainer's summary overlay), so it
 * is vocabulary the user has seen one tap away rather than something new.
 *
 * Deliberately scale-agnostic: the calendar bucket is an hour on day/week and a
 * day on month/year, so naming the bucket would mean two strings per option and
 * a bug the first time a scale is added.
 */
const CALENDAR_CHOICE_SUBTITLES: Record<
  string,
  Partial<Record<CalendarAggregation, MessageDescriptor>>
> = {
  FF: {
    avg: messages.common.averageOverThePeriod,
    max: messages.common.maxOverThePeriod,
  },
  Wind: {
    avg: messages.common.averageOverThePeriod,
    max: messages.common.maxOverThePeriod,
  },
};

export const getCalendarChoiceSubtitle = (
  metric: string | undefined,
  aggregation: CalendarAggregation,
): MessageDescriptor | undefined =>
  metric ? CALENDAR_CHOICE_SUBTITLES[metric]?.[aggregation] : undefined;
